<!-- Плашка лицензии в списке лицензий пользователя -->
<template>
  <div class="package">
    <!-- Бейджик ------------------------------------------------->
    <!-- === Синхронизировать с Редактором === -->
    <template v-if="somethingAwaitingPayment()">
      <template v-if="isExpired()">
        <div class="k-card-badge text-dark">
          <b-icon-exclamation-circle-fill
              class="mr-1"
              variant="warning"
          ></b-icon-exclamation-circle-fill
          >
          {{ $t('license.plate.badge.expired') }}
        </div>
      </template>
      <template v-else>
        <template v-if="isTrial()">
          <div class="k-card-badge">
            <b-icon-clock-history
                class="mr-1"
                variant="info"
            ></b-icon-clock-history>
            {{ $t('license.plate.badge.trial') }}
          </div>
        </template>
        <template v-else-if="hasPaidConnection()">
          <div class="k-card-badge">{{ $t('license.plate.badge.paid_unpaid') }}</div>
        </template>
        <template v-else>
          <div class="k-card-badge">{{ $t('license.plate.badge.draft') }}</div>
        </template>
      </template>
    </template>
    <template v-else>
      <template v-if="isSuspended()"
      >
        <div class="k-card-badge text-dark">
          <b-icon-exclamation-circle-fill
              v-if="!isExpired()"
              class="mr-1"
              variant="info"
          ></b-icon-exclamation-circle-fill
          >
          {{ $t('license.plate.badge.suspended') }}
        </div>
      </template>
      <template v-if="isLocalNames()">
        <div class="k-card-badge text-dark">
          <template v-if="hasPaidConnection()">{{ $t('license.plate.badge.paid') }}, {{ $t('license.plate.badge.locals') }}</template>
          <template v-else>
            <b-icon-clock-history
                class="mr-1"
                variant="info"
            ></b-icon-clock-history>
            {{ $t('license.plate.badge.locals') }}
          </template>
        </div>
      </template>
    </template>
    <!-- <== Конец синхронизации ====== -->

    <!-- Тело ---------------------------------------------------->
    <div class="icon">
      <b-img fluid v-if="!item.encrypt" src="/license.svg"></b-img>
      <b-img fluid v-else src="/encrypted-license.svg"></b-img>
    </div>

    <div class="info">
      <div class="name">{{ getLicenseName(item) }}</div>
      <div v-if="item.description" class="description">
        {{ item.description }}
      </div>
      <div class="summary">
        <template v-if="somethingAwaitingPayment()">
          <!-- Есть за что заплатить -->
          <template v-if="isExpired()">
            <template v-if="isExpirationToday()">
              <div class="next hot">
                {{ $t('license.plate.next.expired_today', [validUntilFormatted]) }} - <span class="expires">{{ $t('license.plate.next.today') }}</span>
              </div>
            </template>
            <template v-else>
              <div class="next">
                {{ $t('license.plate.next.expired_ago', [distance(nearestInPast)]) }}
              </div>
            </template>
          </template>
          <template v-else>
            <template v-if="isTrial()">
              <template v-if="isExpirationToday()">
                <div class="next hot">
                  {{ $t('license.plate.next.valid_until', [validUntilFormatted]) }} - <span class="expires">{{ $t('license.expires_today') }}</span>
                </div>
              </template>
              <template v-else>
                <div class="next">
                  {{ $t('license.plate.next.valid_until', [validUntilFormatted]) }}
                </div>
              </template>
            </template>
          </template>
          <div class="price">{{ $t('license.plate.price', [item._price.summary]) }}</div>
        </template>
        <template v-else>
          <!-- Нет ничего к оплате -->
          <template v-if="isExpired()">
            <div class="next">
              {{ $t('license.plate.next.expired_ago', [distance(nearestInPast)]) }}
            </div>
            <div class="price line-through">
              {{ $t('license.plate.price', [item._price.summary]) }}
            </div>
          </template>
          <template v-else>
            <template v-if="isLocalNames()">
              <div v-if="hasPaidConnection()" class="next">
                {{ nextBill(item) }}
              </div>
              <template v-if="isExpirationToday()">
                <div class="next hot">
                  {{ $t('license.plate.next.valid_until', [validUntilFormatted]) }} - <span class="expires">{{ $t('license.expires_today') }}</span>
                </div>
              </template>
              <template v-else>
                <div class="next">
                  {{ $t('license.plate.next.valid_until', [validUntilFormatted]) }}
                </div>
              </template>
            </template>
            <template v-else>
              <template v-if="isSuspended()">
                <div class="next">{{ $t('license.plate.next.valid_until', [validUntilFormatted]) }}</div>

                <div class="price line-through">
                  {{ $t('license.plate.price', [item._price.summary]) }}
                </div>
              </template>
              <template v-else>
                <template v-if="isExpirationToday()">
                  <div class="next hot">
                    {{ nextBill(item) }} - <span class="today">{{ $t('license.plate.next.today') }}</span>
                  </div>
                </template>
                <template v-else>
                  <div class="next">{{ nextBill(item) }}</div>
                </template>

                <div class="price">{{ $t('license.plate.price', [item._price.summary]) }}</div>
              </template>
            </template>
          </template>
        </template>
      </div>
    </div>
    <!-- Кнопки -->
    <div class="ctrls v-center">
      <div class="buttons">
        <div>
          <b-button v-if="somethingAwaitingPayment()" variant="primary" @click="handlePayClick(item)">
            <b-icon-credit-card-fill class="mr-1"></b-icon-credit-card-fill>
            {{ $t('license.editor.pay') }}
          </b-button>
          <b-button variant="primary" @click="handleEditClick(item)">
            <b-icon-gear aria-hidden="true" class="mr-1"></b-icon-gear>
            {{ $t('license.plate.edit') }}
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {distanceFromMillis} from "@/util/time";
import {
  isTrial,
  getLicenseName,
  somethingAwaitingPayment,
  suspended,
  expired,
  localNames,
  conValidUntilFormatted,
  validUntilFormatted,
  nextBillFormated,
  lastInFutureMillis,
  licenseObject,
  hasPaidConnection,
  isExpirationToday,
} from "@/components/rdbm/license";

import {
  BIconGear,
  BIconExclamationCircleFill,
  BIconClockHistory,
  BIconCreditCardFill
} from "bootstrap-vue";

export default {
  name: "License",
  components: {
    BIconGear,
    BIconExclamationCircleFill,
    BIconClockHistory,
    BIconCreditCardFill
  },
  props: {
    item: {
      type: Object,
      default: null,
    },
    licenses: {
      type: Array,
      default: null,
    },
    id: {
      type: String,
      default: "",
    },
  },
  methods: {
    isTrial: function () {
      return isTrial(this.item);
    },
    isExpired: function () {
      return expired(this.item);
    },
    isSuspended: function () {
      return suspended(this.item);
    },
    isLocalNames: function () {
      return localNames(this.item);
    },
    somethingAwaitingPayment: function () {
      return somethingAwaitingPayment(this.item);
    },
    hasPaidConnection: function () {
      return hasPaidConnection(this.item)
    },
    conValidUntilFormatted: function (conn) {
      return conValidUntilFormatted(conn);
    },
    getLicenseName: function (item) {
      return getLicenseName(item);
    },
    isExpirationToday: function () {
      return isExpirationToday(this.item)
    },
    distance: function (millis) {
      return distanceFromMillis(millis);
    },
    nextBill: function (item) {
      return nextBillFormated(item);
    },
    handleEditClick: function (item) {
      this.$router.push({
        name: "rdbmItem",
        params: {id: item.id, item: item, licenses: this.licenses},
      });
    },
    handlePayClick: function (item) {
      this.$emit("show-pay-dialog", licenseObject.init(item))
    },
  },
  computed: {
    validUntilFormatted: function () {
      return validUntilFormatted(this.item);
    },
    lastInFuture: function () {
      return lastInFutureMillis(this.item);
    },
    nearestInPast: function () {
      let now = Date.now();
      let result = null;
      let found = false;
      this.item.connections.forEach((con) => {
        let t = new Date(con.valid_until).getTime();
        if (t < now && (result == null || t > result)) {
          result = t;
          found = true;
        }
      });
      return found ? result : null;
    },
  },
};
</script>

<style>
.package {
  position: relative;
  display: grid;
  grid-template-columns: 4rem auto auto;
  grid-template-rows: 75% 25%;
  padding: 1.5rem;
  border: var(--default-border);
  border-radius: var(--default-border-radius);
}

@media screen and (max-width: 767px) {
  .package {
    grid-template-columns: 3rem auto auto;
  }
}

@media screen and (max-width: 600px) {
  .package {
    padding: 1.5rem 1rem;
  }
}

.package:not(:last-child) {
  margin: 0 0 2.5em 0;
}

.package .k-card-badge {
  left: calc(1.5rem + 3rem + 1rem - 0.5em + 1px);
  position: absolute;
  top: calc(-0.5rem - 0.225em);
  font-weight: normal;
  font-size: 90%;
  padding: 0 0.5em;
  border-radius: 0.25rem;
  background-color: white;
}

@media screen and (max-width: 767px) {
  .package .k-card-badge {
    left: calc(1.5rem + 2rem + 1rem - 0.5em + 1px);
  }
}

@media screen and (max-width: 600px) {
  .package .k-card-badge {
    left: calc(1rem + 2rem + 1rem - 0.5em + 1px);
  }
}

.package .icon {
  display: flex;
  color: #424242;
  margin-right: 1rem;
  grid-column-start: 1;
  grid-column-end: 2;
  grid-row-start: 1;
  grid-row-end: 3;
}

.package .icon img {
  width: 100%;
  height: 3rem;
}

@media screen and (max-width: 767px) {
  .package .icon {
    font-size: 2rem;
  }

  .package .icon img {
    height: 2rem;
  }
}

.package .info {
  flex-grow: 4;
  align-items: flex-end;
  grid-column-start: 2;
  grid-column-end: 3;
  grid-row-start: 1;
  grid-row-end: 3;
}

@media screen and (max-width: 600px) {
  .package .info {
    grid-column-start: 2;
    grid-column-end: 4;
    grid-row-start: 1;
    grid-row-end: 2;
  }
}

.package .ctrls {
  min-width: 100px;
  padding: 0 0 0 1em;
  grid-column-start: 3;
  grid-column-end: 4;
  grid-row-start: 1;
  grid-row-end: 3;
}

@media screen and (max-width: 600px) {
  .package .ctrls {
    padding: 0.5em 0 0 0;
    grid-column-start: 2;
    grid-column-end: 4;
    grid-row-start: 2;
    grid-row-end: 3;
  }
}

.package .ctrls div {
  justify-content: flex-end;
  display: flex;
}

@media screen and (max-width: 600px) {
  .package .ctrls div {
    justify-content: flex-start;
    display: flex;
  }
}

@media screen and (max-width: 400px) {
  .package button svg {
    display: none !important;
  }
}

.package .description {
  font-size: 90%;
  opacity: 0.75;
}

.package .summary {
  margin-top: 0.5em;
  font-size: 90%;
}

.package .summary .next:not([class*="hot"]),
.package .summary .price {
  opacity: 0.65;
}

.package .summary .price {
  margin-top: 0.5rem;
}

.suspended .icon {
  opacity: 0.65;
}

.suspended .info {
  opacity: 0.75;
}
</style>